import React, { useEffect, useState } from 'react';
import { useAgents } from '../hooks/use-agents';
import { AgentForm, AgentCard, AgentTester } from '../components/agents';
import type { Agent } from '../lib/agent-types';

export function AgentBuilderPage() {
  const { agents, loading, error, fetchAgents, createAgent, editAgent, deleteAgent, publishAgent, unpublishAgent } = useAgents();
  const [editingAgent, setEditingAgent] = useState<Agent | null>(null);
  const [testingAgent, setTestingAgent] = useState<Agent | null>(null);
  const [view, setView] = useState<'create' | 'list'>('list');

  useEffect(() => { fetchAgents(); }, [fetchAgents]);

  const handleCreate = async (data: any) => {
    await createAgent(data);
    setView('list');
  };

  const handleUpdate = async (data: any) => {
    if (editingAgent) {
      await editAgent(editingAgent.id, data);
      setEditingAgent(null);
    }
  };

  const handleDelete = async (id: string) => {
    if (confirm('Delete this agent?')) {
      await deleteAgent(id);
    }
  };

  const handlePublish = async (id: string) => {
    const agent = agents.find(a => a.id === id);
    if (agent) {
      agent.published ? await unpublishAgent(id) : await publishAgent(id);
    }
  };

  if (loading && agents.length === 0) return <div className="p-8 text-center">Loading...</div>;
  if (error) return <div className="p-8 text-red-600">{error}</div>;

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow">
        <div className="max-w-7xl mx-auto py-6 px-4">
          <h1 className="text-3xl font-bold text-gray-900">Agent Builder</h1>
        </div>
      </header>
      
      <main className="max-w-7xl mx-auto py-6 px-4">
        {/* Navigation tabs */}
        <div className="flex gap-4 mb-6">
          <button
            onClick={() => setView('list')}
            className={`px-4 py-2 rounded-md ${view === 'list' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 border'}`}
          >
            My Agents ({agents.length})
          </button>
          <button
            onClick={() => setView('create')}
            className={`px-4 py-2 rounded-md ${view === 'create' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 border'}`}
          >
            + Create Agent
          </button>
        </div>

        {view === 'create' && !editingAgent && (
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold mb-4">Create New Agent</h2>
            <AgentForm onSubmit={handleCreate} onCancel={() => setView('list')} loading={loading} />
          </div>
        )}

        {view === 'list' && !editingAgent && (
          <>
            {agents.length === 0 ? (
              <div className="bg-white rounded-lg shadow p-8 text-center">
                <p className="text-gray-500 mb-4">No agents yet. Create your first agent!</p>
                <button onClick={() => setView('create')} className="px-4 py-2 bg-blue-600 text-white rounded-md">
                  Create Agent
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {agents.map(agent => (
                  <AgentCard
                    key={agent.id}
                    agent={agent}
                    onEdit={setEditingAgent}
                    onDelete={handleDelete}
                    onPublish={handlePublish}
                    onTest={setTestingAgent}
                  />
                ))}
              </div>
            )}
          </>
        )}

        {editingAgent && (
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold mb-4">Edit Agent</h2>
            <AgentForm
              initialData={editingAgent}
              onSubmit={handleUpdate}
              onCancel={() => setEditingAgent(null)}
              loading={loading}
            />
          </div>
        )}
      </main>

      {testingAgent && (
        <AgentTester agent={testingAgent} onClose={() => setTestingAgent(null)} />
      )}
    </div>
  );
}
